import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";

export type TourStep = {
  selector: string;
  title?: string;
  content: string;
};

export function GuidedTour({
  open,
  steps,
  onClose,
  storageKey,
}: {
  storageKey: string;
  open: boolean;
  steps: TourStep[];
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [_, setDismissed] = useLocalStorage<boolean>(
    `${storageKey}-dismissed`,
    false
  );
  const { t } = useTranslation("common");
  useLayoutEffect(() => {
    if (!open || steps.length === 0) return;
    const step = steps[index];
    const el = document.querySelector(step.selector) as HTMLElement | null;
    setTargetEl(el);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      const r = el.getBoundingClientRect();
      setRect(r);
    } else {
      const next = index + 1;
      if (next < steps.length) setIndex(next);
      else onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, steps.map((s) => s.selector).join("|")]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (targetEl) setRect(targetEl.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const ro = new ResizeObserver(update);
    if (targetEl) ro.observe(targetEl);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      ro.disconnect();
    };
  }, [open, targetEl]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setIndex((i) => Math.min(i + 1, steps.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, steps.length, onClose]);

  const step = steps[index];
  const tooltipPos = useMemo(() => {
    if (!rect) return { top: 0, left: 0, placement: "center" as const };
    const margin = 12;
    const below = rect.bottom + 160 < window.innerHeight;
    const top = below
      ? rect.bottom + margin
      : Math.max(12, rect.top - 12 - 120);
    const center = Math.min(
      Math.max(12, rect.left + rect.width / 2 - 160),
      window.innerWidth - 320 - 12
    );
    return {
      top,
      left: center,
      placement: below ? "below" : ("above" as const),
    };
  }, [rect]);

  if (!open || !step || !rect) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
        }}
      />

      <div
        id="tour-highlight"
        aria-hidden
        className="rounded-sm absolute pointer-events-none outline outline-foreground shadow-[0_0_0_9999px_rgba(0,0,0,0.45),0_8px_24px_rgba(0,0,0,0.35)]"
        style={{
          top: rect.top - 12,
          left: rect.left - 1,
          width: rect.width + 16,
          height: rect.height + 16,
          transition: "all 180ms ease",
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="px-4 py-3 rounded-sm bg-background text-foreground shadow-[0_10px_30px_rgba(0,0,0,0.2)] absolute"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
        }}
      >
        {step.title ? (
          <h1 className="text-lg font-semibold">{step.title}</h1>
        ) : null}
        <div
          style={{ fontSize: 14, lineHeight: 1.4 }}
          className="max-w-screen-sm whitespace-pre-line"
        >
          {step.content}
        </div>
        <div className="flex justify-between gap-2 mt-3">
          <div className="text-sm text-muted-foreground">
            Step {index + 1} / {steps.length}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="button"
              variant={"secondary"}
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={index === 0}
            >
              {t("common:back")}
            </Button>
            {index < steps.length - 1 ? (
              <Button
                variant={"default"}
                type="button"
                onClick={() =>
                  setIndex((i) => Math.min(i + 1, steps.length - 1))
                }
              >
                {t("common:next")}
              </Button>
            ) : (
              <Button
                variant={"default"}
                type="button"
                onClick={() => {
                  onClose();
                  setIndex(0);
                  setDismissed(true);
                }}
              >
                {t("common:finish")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
